/**
 * Source of truth for the per-evidence weights that roll up into the GCA
 * and Comp Way axes, plus their default thresholds.
 *
 * Kept in `shared/` so the client can render current weights on every page
 * — including stored evaluations whose `evidence.contributions[].weight`
 * was frozen at evaluation time. Without this shared module, the client
 * displayed stale weights on old evals (the aggregator's math used the
 * current constants, but the chip labels still showed the old numbers).
 *
 * To rebalance an axis: edit `AXIS_EVIDENCE_MAP` here and re-deploy. New
 * evaluations score against the new weights immediately; stored
 * evaluations show the new weights in the UI right away (the math runs
 * fresh on every read via the hydration path).
 *
 * v2 cutover (2026-05-07): `company_era` and `growth_within` were merged
 * into a single `trajectory` dim. Historical evaluations carry the legacy
 * weights frozen on their evidence rows at evaluation time.
 */

import type { AxisId } from "./three-axis";

export const AXIS_EVIDENCE_MAP: Record<
  Exclude<AxisId, "skills">,
  { dim: string; weight: number }[]
> = {
  gca: [
    { dim: "academic", weight: 0.3 },
    { dim: "trajectory", weight: 0.7 },
  ],
  compWay: [
    { dim: "founder", weight: 0.4 },
    { dim: "tenure", weight: 0.3 },
    { dim: "trajectory", weight: 0.3 },
  ],
};

export const DEFAULT_AXIS_THRESHOLDS: Record<Exclude<AxisId, "skills">, number> = {
  gca: 0.6,
  compWay: 0.65,
};

/** Look up the current weight a given dim contributes to a given axis,
 *  or 0 if the dim doesn't feed the axis at all. */
export function currentAxisWeight(axisId: Exclude<AxisId, "skills">, dim: string): number {
  return AXIS_EVIDENCE_MAP[axisId].find((e) => e.dim === dim)?.weight ?? 0;
}
